import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Users } from 'lucide-react';
import { api } from '../../services/api';
import DetailSection from '../details/DetailSection';
import DataTable, { type Column } from '../DataTable';
import StateBlock from '../ui/StateBlock';
import ConfirmModal from '../ConfirmModal';
import EmptyState from '../EmptyState';
import { apiErrorMessage } from '../sales/shared';
import { toast } from '../../store/toastStore';
import { hoursText } from './projectModel';
import type { ProjectTeamPerson } from '../../types';
import { withoutInternalOnlyOnCustomerWork } from '../../utils/customerWorkPeople';
import EntityPicker from '../ui/EntityPicker';

/**
 * P-04/C (D-39) — the "צוות" tab: the project manager, the team lead, the
 * people on its milestones and on its tasks, each once with their role(s) and
 * their hours in the project. Adding / removing a team member by hand works
 * as before (project_members). The project manager and the team lead are set
 * in the project form ([✎ עריכה]).
 */
export default function ProjectTeamTab({ projectId, canManage, customerProject = true }: { projectId: string; canManage: boolean; customerProject?: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['project-team', projectId],
    queryFn: () => api.getProjectTeam(projectId),
  });
  const people: ProjectTeamPerson[] = data?.data ?? [];

  const { data: employeesRes } = useQuery({
    queryKey: ['users', 'ote_staff', 'all-active'],
    queryFn: () => api.getUsers({ ote_staff: true, limit: 200 }),
    staleTime: 60_000,
    enabled: canManage,
  });
  // FIX-B17 (D-198 QX-152) — a customer's project team has no "פנימי בלבד" employee.
  const candidates = withoutInternalOnlyOnCustomerWork((employeesRes?.data ?? []) as any[], customerProject)
    .filter((u: any) => u.active !== false && !people.some((p) => p.user_id === u.id && p.member))
    .map((u: any) => ({ id: u.id as string, name: (u.name || u.email) as string }))
    .sort((a, b) => a.name.localeCompare(b.name, 'he'));

  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState('');
  const [removing, setRemoving] = useState<ProjectTeamPerson | null>(null);
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['project-team', projectId] });
    queryClient.invalidateQueries({ queryKey: ['project', projectId] });
  };
  const add = useMutation({
    mutationFn: () => api.addProjectMember(projectId, selected),
    onSuccess: () => { setSelected(''); setAdding(false); refresh(); },
    onError: (e) => toast('error', apiErrorMessage(e, t('project.teamMutationError'))),
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api.removeProjectMember(projectId, userId),
    onSuccess: refresh,
    onError: (e) => toast('error', apiErrorMessage(e, t('project.teamMutationError'))),
  });

  if (isLoading) return <StateBlock kind="loading" />;
  if (error && !data) return <StateBlock kind="error" error={error} title={t('projectPage.team.loadError')} onRetry={() => refetch()} retrying={isFetching} />;

  // P-22 (D-156 #8) - Roni's table (DataTable v2): same columns as before, cards on a phone.
  const columns: Column<ProjectTeamPerson>[] = [
    { key: 'person', header: t('projectPage.team.person'), render: (p) => <bdi>{p.name ?? p.email ?? '—'}</bdi> },
    { key: 'role', header: t('projectPage.team.role'), render: (p) => <span className="text-text-2">{p.roles.map((r) => t(`projectPage.team.roles.${r}`)).join(' · ')}</span> },
    { key: 'hours', header: t('projectPage.team.hours'), align: 'end', render: (p) => <span className="tabular-nums">{hoursText(p.hours)}</span> },
  ];

  return (
    <DetailSection
      title={t('project.teamTab')}
      actions={canManage && !adding ? (
        <button type="button" onClick={() => setAdding(true)} className="btn btn-secondary btn-sm">
          <Plus size={14} aria-hidden="true" /> {t('project.addMember')}
        </button>
      ) : undefined}
      flush
    >
      {adding && (
        <div className="flex flex-wrap items-center gap-2 border-b border-surface-200 bg-surface-50 px-4 py-3 sm:px-5">
          <label htmlFor="team-add-member" className="sr-only">{t('project.selectUser')}</label>
          <EntityPicker
            id="team-add-member"
            className="min-w-0 flex-1"
            placeholder={t('project.selectUser')}
            options={candidates.map((u) => ({ value: u.id, label: u.name }))}
            value={selected}
            onChange={setSelected}
          />
          <button type="button" onClick={() => setAdding(false)} className="btn btn-ghost">{t('common.cancel')}</button>
          <button type="button" onClick={() => add.mutate()} disabled={!selected || add.isPending} className="btn btn-primary">{t('common.add')}</button>
        </div>
      )}
      {people.length === 0 ? (
        <EmptyState icon={Users} title={t('project.noMembers')} />
      ) : (
        <DataTable
          columns={columns}
          data={people}
          keyField="user_id"
          caption={t('project.teamTab')}
          mobile="cards"
          rowLabel={(p) => p.name ?? p.email ?? ''}
          rowActions={canManage ? (p) => (p.member ? [{
            key: 'remove',
            label: t('projectPage.team.removeMember'),
            icon: <Trash2 size={16} strokeWidth={1.75} />,
            danger: true,
            inline: true,
            disabled: remove.isPending,
            onSelect: () => setRemoving(p),
          }] : []) : undefined}
        />
      )}
      {removing && (
        <ConfirmModal
          title={t('project.removeMemberTitle')}
          description={t('project.removeMemberConfirm', { name: removing.name || '' })}
          confirmLabel={t('common.remove')}
          danger
          onConfirm={() => { remove.mutate(removing.user_id); setRemoving(null); }}
          onCancel={() => setRemoving(null)}
        />
      )}
    </DetailSection>
  );
}
